/**
 * The first sign-in's card (People Part 5; the chair app's onboarding home on the canvas):
 * "how this works" — who you are this year, your area and co-chairs, the four things you will
 * be asked on a festival day, your compliance as states with dates, the meeting dates, who the
 * directors are and where to ask. A passkey is offered, never forced (directors and the finance
 * director already set a second factor before this). One tap opens the app where it opens.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Chip, formatDate, formatDateTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { areaWords } from '../../shell/words.js';
import { LoginFrame } from './Login.js';
import { SecondFactor } from './SecondFactor.js';

interface Card {
  name: string;
  festivalYear: number;
  roles: { role: string; roleName: string; areaId: string | null; areaName: string | null }[];
  areas: { id: string; name: string }[];
  coChairs: { personId: string; name: string; areaName: string }[];
  fourThings: { what: string; where: string }[];
  compliance: { kind: string; state: string; validUntil: string | null }[];
  meetings: { id: string; at: string; title: string }[];
  directors: { personId: string; name: string; roleName: string }[];
  askAt: string;
  opensAt: string;
  secondFactor: 'set' | 'none';
  packet: { left: number; says: string } | null;
}

const KIND: Record<string, string> = { background_check: 'Background check', training: 'Training' };
const STATE: Record<string, string> = {
  requested: 'requested',
  cleared: 'cleared',
  expired: 'expired',
  not_required: 'not required',
};

export function Onboarding({
  onDone,
  onSignOut,
}: {
  onDone: (opensAt: string) => Promise<void>;
  onSignOut: () => Promise<void>;
}): ReactNode {
  const [card, setCard] = useState<Card | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [passkey, setPasskey] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<Card>('GET', '/auth/onboarding')
      .then(setCard)
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      });
  }, [passkey]);
  if (passkey)
    return (
      <SecondFactor
        mode="set_one"
        onDone={() => {
          setPasskey(false);
          return Promise.resolve();
        }}
        onSignOut={onSignOut}
      />
    );
  if (!card)
    return (
      <LoginFrame>
        <h1>How this works</h1>
        <p className="charter">One card, once.</p>
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : (
          <p>Reading your card…</p>
        )}
      </LoginFrame>
    );
  const area = card.areas.map((a) => a.name).join(', ');
  return (
    <LoginFrame>
      <h1>Welcome, {card.name}</h1>
      <p className="charter">
        How this works for {String(card.festivalYear)}. One card, once; everything on it is a tap
        away in the app.
      </p>
      <div className="onboarding" data-screen="onboarding">
        <section>
          <h2>This year you are</h2>
          <ul>
            {card.roles.map((r) => (
              <li key={`${r.role}|${r.areaId ?? ''}`}>
                <b>{r.roleName}</b>
                {r.areaId ? ` · ${areaWords(r.areaName, r.areaId)}` : ''}
              </li>
            ))}
          </ul>
          {card.areas.length > 0 ? (
            <p>
              Your area: <b>{area}</b>.{' '}
              {card.coChairs.length > 0
                ? `With you: ${card.coChairs.map((c) => `${c.name} (${c.areaName})`).join(', ')}.`
                : 'No co-chair yet.'}
            </p>
          ) : null}
        </section>
        <section>
          <h2>The four things you will be asked on a festival day</h2>
          <ol>
            {card.fourThings.map((t) => (
              <li key={t.what}>
                {t.what} <span className="onboarding__where">— {t.where}</span>
              </li>
            ))}
          </ol>
          <p>Each one tap. Nothing is required of you off-season.</p>
        </section>
        <section>
          <h2>What the role must have</h2>
          {card.compliance.length === 0 ? (
            <p>
              Nothing on file yet; the volunteer chairs will tell you if the role needs a check.
            </p>
          ) : (
            <ul>
              {card.compliance.map((c) => (
                <li key={c.kind}>
                  {KIND[c.kind] ?? c.kind}:{' '}
                  <Chip kind={c.state === 'cleared' || c.state === 'not_required' ? 'ok' : 'warn'}>
                    {STATE[c.state] ?? c.state}
                  </Chip>
                  {c.validUntil ? ` · good until ${formatDate(c.validUntil)}` : ''}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h2>Meetings</h2>
          {card.meetings.length === 0 ? (
            <p>The year's meeting dates are not set yet.</p>
          ) : (
            <ul>
              {card.meetings.slice(0, 12).map((m) => (
                <li key={m.id}>
                  {formatDateTime(m.at)} · {m.title}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h2>Your directors, and where to ask</h2>
          <ul>
            {card.directors.map((d) => (
              <li key={d.personId}>
                {d.name} · {d.roleName}
              </li>
            ))}
          </ul>
          <p>
            Anything at all: write to <b>{card.askAt}</b>.
          </p>
        </section>
        {card.packet ? (
          <section>
            <h2>Your {String(card.festivalYear)} packet</h2>
            <p>
              {card.packet.says}. About ten minutes on your phone — names, how to reach you, your
              family, your role, the waiver, your kit. It opens after this card.
            </p>
          </section>
        ) : null}
        <section>
          <h2>Sign-in</h2>
          {card.secondFactor === 'set' ? (
            <p>A passkey or authenticator is set on your account.</p>
          ) : (
            <p>
              A passkey lets you sign in with your face or fingerprint. Optional for you.{' '}
              <Button
                onClick={() => {
                  setPasskey(true);
                }}
              >
                Set a passkey
              </Button>
            </p>
          )}
        </section>
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        <Button
          kind="primary"
          busy={busy}
          onClick={() => {
            setBusy(true);
            api('POST', '/auth/onboarding/done')
              .then(() =>
                onDone(
                  card.packet && card.packet.left > 0 ? '/app/more/your-packet' : card.opensAt,
                ),
              )
              .catch((e: unknown) => {
                setRefusal(sentence(e));
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          {card.packet && card.packet.left > 0
            ? 'Start your packet'
            : card.opensAt === '/app'
              ? 'Open the chair app'
              : 'Open CIDER'}
        </Button>
      </div>
    </LoginFrame>
  );
}
